import "@hotwired/turbo-rails";
import "preline/dist/preline";

import { Application } from "@hotwired/stimulus";
import ModalComponentController from "./controllers/modal_component_controller";
import SearchableSelectInputController from "./controllers/searchable_select_input_controller";
import DatepickerInputController from "./controllers/datepicker_input_controller";
import ToatComponentController from "./controllers/toast_component_controller";
import ChatComponentController from "./controllers/chat_component_controller";
import ClickableRowController from "./controllers/clickable_row_controller";
import MessagesIndexController from "./controllers/messages_index_controller";
import MessageSetFolderSelectController from "./controllers/message_set_folder_select_controller";
import FileInputController from "./controllers/file_input_controller";
import ClipboardController from "./controllers/clipboard_controller";
import GdprExportController from "./controllers/gdpr_export_controller";
import PreserveScrollController from "./controllers/preserve_scroll_controller";
import FolderPathPickerController from "./controllers/folder_path_picker_controller";
import UserChannelFieldsController from "./controllers/user_channel_fields_controller";
import EmojiPickerController from "./controllers/emoji_picker_controller";
import NewConversationPickerController from "./controllers/new_conversation_picker_controller";
import MessageSetsBulkController from "./controllers/message_sets_bulk_controller";

const application = Application.start();
application.debug = false;
window.Stimulus = application;

application.register("modal-component", ModalComponentController);
application.register("toast-component", ToatComponentController);
application.register("datepicker-input", DatepickerInputController);
application.register("chat-component", ChatComponentController);
application.register(
  "searchable-select-input",
  SearchableSelectInputController
);
application.register("clickable-row", ClickableRowController);
application.register("messages-index", MessagesIndexController);
application.register("message-set-folder-select", MessageSetFolderSelectController);
application.register("file-input", FileInputController);
application.register("clipboard", ClipboardController);
application.register("gdpr-export", GdprExportController);
application.register("preserve-scroll", PreserveScrollController);
application.register("folder-path-picker", FolderPathPickerController);
application.register("user-channel-fields", UserChannelFieldsController);
application.register("emoji-picker", EmojiPickerController);
application.register("new-conversation-picker", NewConversationPickerController);
application.register("message-sets-bulk", MessageSetsBulkController);

const init = () => {
  HSDropdown.autoInit();
};

// Once: click delegation on document — also works for content loaded via Turbo Frame
let messageSetBrowserDelegationDone = false;
function setupMessageSetFolderBrowserDelegation() {
  if (messageSetBrowserDelegationDone) return;
  messageSetBrowserDelegationDone = true;
  document.addEventListener("click", (ev) => {
    const row = ev.target.closest(".message-set-folder-browser .set-row");
    if (!row) return;
    const container = row.closest(".message-set-folder-browser");
    if (!container) return;
    ev.preventDefault();
    ev.stopPropagation();
    const form = container.closest("form");
    if (!form) return;
    const templateId = row.getAttribute("data-root-template-id");
    let inp;
    let value;
    if (templateId) {
      inp =
        form.querySelector('input[name="message[message_template_id]"]') ||
        form.querySelector("#message_message_template_id_choice");
      value = templateId;
    } else {
      inp =
        form.querySelector('input[name="playbook_schedule[message_set_id]"]') ||
        form.querySelector("#playbook_schedule_message_set_id_choice") ||
        form.querySelector('input[name="message_schedule[message_set_id]"]') ||
        form.querySelector("#message_schedule_message_set_id_choice");
      value = row.getAttribute("data-set-id");
    }
    if (!inp || !value) return;
    inp.value = value;
    // Highlight via inline style (Tailwind classes are often purged)
    container.querySelectorAll(".set-row").forEach((r) => {
      r.removeAttribute("data-selected");
      r.style.backgroundColor = "";
      r.style.outline = "";
      r.style.outlineOffset = "";
    });
    row.setAttribute("data-selected", "true");
    row.style.backgroundColor = "rgb(239 246 255)";
    row.style.outline = "2px solid rgb(96 165 250)";
    row.style.outlineOffset = "2px";
    // If inside a modal: set the summary text and collapse details
    const details = container.closest(".message-set-folder-dropdown");
    if (details) {
      const nameEl = details.querySelector("[data-message-set-name]");
      const name = row.querySelector(".set-name")?.textContent?.trim() || "";
      if (nameEl) nameEl.textContent = name || "Choose Message Set...";
      details.open = false;
    }
  }, true);
}

const VIDEO_FILENAME_EXT = /\.(mp4|m4v|webm|mov|mkv|avi|ogv)$/i;

function setupMediaVideoThumbnailToggle() {
  document.querySelectorAll("[data-media-upload-with-thumbnail]").forEach((root) => {
    const mainInput = root.querySelector('input[type="file"][name="media_asset[file]"]');
    const section = root.querySelector("[data-media-thumb-section]");
    const thumbInput = root.querySelector("[data-media-thumb-input]");
    if (!mainInput || !section) return;
    if (mainInput.dataset.mediaVideoThumbBound === "1") return;
    mainInput.dataset.mediaVideoThumbBound = "1";

    const sync = () => {
      const file = mainInput.files?.[0];
      const isVideo =
        !!file &&
        (file.type.startsWith("video/") || VIDEO_FILENAME_EXT.test(file.name));

      if (isVideo) {
        section.classList.remove("hidden");
      } else {
        section.classList.add("hidden");
        if (thumbInput) thumbInput.value = "";
      }
    };

    mainInput.addEventListener("change", sync);
    sync();
  });
}

function setupFolderDisclosurePersistence() {
  const path = (window.location.pathname || "/").replace(/\/+$/, "") || "/";

  document
    .querySelectorAll("details.folder-disclosure[data-folder-disclosure-key]")
    .forEach((detailsEl) => {
      if (detailsEl.dataset.folderDisclosureBound === "1") return;
      detailsEl.dataset.folderDisclosureBound = "1";

      const key = detailsEl.getAttribute("data-folder-disclosure-key");
      if (!key) return;

      const storageKey = `folderDisclosure:v1:${path}:${encodeURIComponent(key)}`;
      const stored = localStorage.getItem(storageKey);
      if (stored === "1") detailsEl.open = true;
      if (stored === "0") detailsEl.open = false;

      detailsEl.addEventListener("toggle", () => {
        localStorage.setItem(storageKey, detailsEl.open ? "1" : "0");
      });
    });
}

function onPageLoad() {
  setupMessageSetFolderBrowserDelegation();
  setupMediaVideoThumbnailToggle();
  setupFolderDisclosurePersistence();
  try {
    init();
  } catch (e) {
    console.warn("[PlaybookSchedule] init() error:", e);
  }
}

document.addEventListener("turbo:load", onPageLoad);
// Fallback if the page is loaded without Turbo
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", onPageLoad);
} else {
  onPageLoad();
}

export { application };
